import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { apiFetch } from '@/api/client'
import type {
  BlockedDevicePlayer,
  BlockedDeviceQuery,
  BlockedDeviceResult,
  BlockedDeviceRow,
} from '@/features/logs/blockedDeviceTypes'

/** Agent-API `POST /v1/blockedDevices/playersWithBlockedDevices` request. */
export type PlayersWithBlockedDevicesRequest = {
  page?: number
  pageSize?: number
  deviceId?: string
  player?: string
}

export type BlockedDeviceApiPlayer = {
  username?: string
  accessLevel?: string
  siteUrl?: string
}

/** One Lab/Agent-API blocked-device row (Winner + Backend-UI fields). */
export type BlockedDeviceApiRow = {
  id?: string
  _id?: string
  deviceId?: string
  comment?: string
  playerCount?: number
  players?: BlockedDeviceApiPlayer[]
  user?: BlockedDeviceApiPlayer[]
  createdAt?: string
}

/** Lab/Winner callback envelope from hierarchy-service. */
export type PlayersWithBlockedDevicesResponse = {
  value: boolean
  data: {
    dataArray?: BlockedDeviceApiRow[]
    results?: BlockedDeviceApiRow[]
    totalCount?: number
    total?: number
    limit?: number
    count?: number
    noOfPages?: number
    page?: number
  }
}

/** Winner `BlockedIp/saveBlockedIp` + `deleteBlockedIp` Lab envelope. */
export type BlockedIpWriteResponse = {
  value: boolean
  data?: {
    id?: string
    _id?: string
    deviceId?: string
    IP?: string
    ip?: string
    comment?: string
    createdAt?: string
    createdBy?: string | null
  }
  error?: string
}

/**
 * Winner USER-V2 `blockedDevices/playersWithBlockedDevices` → Agent-API hierarchy.
 */
export async function playersWithBlockedDevices(
  body: PlayersWithBlockedDevicesRequest = {},
): Promise<PlayersWithBlockedDevicesResponse> {
  const out: Record<string, unknown> = {
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  const deviceId = body.deviceId?.trim()
  const player = body.player?.trim()
  if (deviceId) out.deviceId = deviceId
  if (player) out.player = player

  return apiFetch<PlayersWithBlockedDevicesResponse>(
    '/v1/blockedDevices/playersWithBlockedDevices',
    {
      method: 'POST',
      body: JSON.stringify(out),
    },
  )
}

/**
 * Winner USER-V2 `BlockedIp/saveBlockedIp` → Agent-API hierarchy.
 * Body: `{ deviceId, comment? }`.
 */
export async function saveBlockedIp(body: {
  deviceId: string
  comment?: string
}): Promise<BlockedIpWriteResponse> {
  const out: Record<string, unknown> = {
    deviceId: body.deviceId.trim(),
  }
  if (body.comment != null) out.comment = body.comment
  return apiFetch<BlockedIpWriteResponse>('/v1/blockedIp/saveBlockedIp', {
    method: 'POST',
    body: JSON.stringify(out),
  })
}

/**
 * Winner USER-V2 `BlockedIp/deleteBlockedIp` → Agent-API hierarchy.
 * Body: `{ _id }` (blocked_devices uuid).
 */
export async function deleteBlockedIp(body: {
  _id: string
  deviceId?: string
  IP?: string
  ip?: string
  comment?: string
}): Promise<BlockedIpWriteResponse> {
  const out: Record<string, unknown> = { _id: body._id }
  if (body.deviceId != null) out.deviceId = body.deviceId
  if (body.IP != null) out.IP = body.IP
  if (body.ip != null) out.ip = body.ip
  if (body.comment != null) out.comment = body.comment
  return apiFetch<BlockedIpWriteResponse>('/v1/blockedIp/deleteBlockedIp', {
    method: 'POST',
    body: JSON.stringify(out),
  })
}

/** Map hierarchy rows → console Blocked Device Ids table. */
export function toBlockedDeviceResult(
  live: PlayersWithBlockedDevicesResponse,
  query: BlockedDeviceQuery = {},
): BlockedDeviceResult {
  const pageSize =
    live.data?.limit ?? live.data?.count ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = live.data?.page ?? query.page ?? 1
  const total = live.data?.totalCount ?? live.data?.total ?? 0
  const raw = live.data?.dataArray ?? live.data?.results ?? []
  const rows: BlockedDeviceRow[] = raw.map(mapRow)
  return { rows, total, page, pageSize }
}

function mapRow(row: BlockedDeviceApiRow): BlockedDeviceRow {
  const playersRaw = row.players ?? row.user ?? []
  const players: BlockedDevicePlayer[] = []
  for (const p of playersRaw) {
    const mapped = mapPlayer(p)
    if (mapped) players.push(mapped)
  }

  return {
    id: String(row.id ?? row._id ?? ''),
    deviceId: String(row.deviceId ?? ''),
    comment: String(row.comment ?? ''),
    playerCount: row.playerCount ?? players.length,
    players,
  }
}

function mapPlayer(p: BlockedDeviceApiPlayer | undefined): BlockedDevicePlayer | null {
  const username = String(p?.username ?? '').trim()
  if (!username) return null
  const out: BlockedDevicePlayer = { username }
  const accessLevel = p?.accessLevel?.trim()
  if (accessLevel) out.accessLevel = accessLevel
  const siteUrl = p?.siteUrl?.trim()
  if (siteUrl) out.siteUrl = siteUrl
  return out
}
